import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  DashboardsColumnDialogProp,
  DashboardsContextData,
  DashboardsData,
  DashboardsDialogProp,
  DashboardsProviderProps,
} from './types';
import { Emptied } from '@utils/types';
import { initialState } from './constants';
import { useGetDashboardsColumns } from '@services/dashboards/columns';
import { DashboardsColumn, Preset } from '@services/dashboards/types';
import { useGetDashboardsPreview } from '@services/dashboards/preview';
import { useGetPresets } from '@services/dashboards/preset';
import {
  appendId,
  appendPresetColumns,
  parseScope,
  parseSource,
  getSavedTextSize,
  reconcileColumnIds,
  removeAppearance,
} from './helpers';
import { useAppContext } from '@contexts/app';
import { useQueryParams } from '@hooks/queryParams';

const DashboardsContext = createContext<Emptied<DashboardsContextData>>({});

export const DashboardsProvider = ({ children }: DashboardsProviderProps) => {
  const [state, setState] = useState<DashboardsData>(initialState);

  const {
    params: { search },
  } = useQueryParams();

  const {
    dialog: { column },
    selectedColumns,
    initialColumns,
    sorting,
    source,
  } = state;

  const presets = useGetPresets();

  const { displayOptions } = useAppContext();
  const savedTextSize = getSavedTextSize(displayOptions);

  useEffect(() => {
    setState((prev) => ({ ...prev, textSize: savedTextSize }));
  }, [savedTextSize]);

  const columns = useGetDashboardsColumns(parseSource(source), column);

  const preview = useGetDashboardsPreview({
    callback: ({ records }) => {
      if (!records) return;
      setState((prev) => ({ ...prev, preview: records }));
    },
  });

  useEffect(() => {
    if (
      JSON.stringify(selectedColumns) ===
      JSON.stringify(
        appendPresetColumns(
          state.columns.records,
          presets.data?.records?.find((a) => a.id === state.presetId)?.columns,
        ),
      )
    )
      return;
    setState((prev) => ({
      ...prev,
      saved: false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns]);

  const previewColumns = useMemo(() => removeAppearance(selectedColumns), [selectedColumns]);
  const previewColumnsKey = JSON.stringify(previewColumns);

  useEffect(() => {
    if (!previewColumns.length) return;
    preview.mutate({ columns: previewColumns, sorting, scope: parseSource(state.source) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewColumnsKey, sorting, search]);

  useEffect(() => {
    if (!columns?.data?.records) return;
    const newColumns = appendId(columns.data?.records || [], initialColumns);

    setState((prev) => ({
      ...prev,
      initialColumns: state.columns.records.some((el) => el.uuid)
        ? prev.initialColumns
        : newColumns,
      columns: {
        records: newColumns,
        isLoading: false,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns?.data?.records]);

  useEffect(() => {
    if (!presets.data?.records) return;
    const presetId = !state.presetId
      ? presets.data.records.find((el) => el.defaultView)?.id || ''
      : state.presetId;

    const preset = presets.data?.records.find((a) => a.id === presetId);

    const newColumns = appendPresetColumns(state.columns.records, preset?.columns);

    setState((prev) => ({
      ...prev,
      presetId,
      selectedColumns: newColumns,
      checkedColumns: newColumns,
      sorting: preset?.sorting || prev.sorting,
      source: parseScope(preset?.scope),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.presetId, presets.data?.records]);

  useEffect(() => {
    if (!column.details) return;
    setState((prev) => ({
      ...prev,
      checkedColumns: selectedColumns,
    }));
  }, [column.details, selectedColumns]);

  useEffect(() => {
    if (column.edit || !state.columns.records.length) return;
    setState((prev) => {
      const newSelectedColumns = reconcileColumnIds(prev.selectedColumns, prev.columns.records);
      const newCheckedColumns = reconcileColumnIds(prev.checkedColumns, prev.columns.records);
      if (newSelectedColumns === prev.selectedColumns && newCheckedColumns === prev.checkedColumns)
        return prev;
      return { ...prev, selectedColumns: newSelectedColumns, checkedColumns: newCheckedColumns };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columns.records, column.edit]);

  const columnsFailed = Boolean(columns.error) && !columns.data;
  const presetsFailed = Boolean(presets.error) && !presets.data;
  const error = columnsFailed || presetsFailed || preview.isError;

  const retry = () => {
    if (columnsFailed) void columns.refetch();
    if (presetsFailed) void presets.refetch();
    if (preview.isError && previewColumns.length)
      preview.mutate({ columns: previewColumns, sorting, scope: parseSource(state.source) });
  };

  const setPresetDialog = (type: DashboardsDialogProp, value: boolean, values?: Emptied<Preset>) =>
    setState((prev) => ({
      ...prev,
      dialog: {
        ...prev.dialog,
        preset: {
          ...prev.dialog.preset,
          [type]: value,
          values: values || prev.dialog.preset.values,
        },
      },
    }));

  const setColumnDialog = (
    type: DashboardsColumnDialogProp,
    value: boolean,
    values?: Emptied<DashboardsColumn>,
  ) =>
    setState((prev) => ({
      ...prev,
      dialog: {
        ...prev.dialog,
        column: {
          ...prev.dialog.column,
          [type]: value,
          values: values || prev.dialog.column.values,
        },
      },
    }));

  return (
    <DashboardsContext.Provider
      value={{
        ...state,
        isLoading: preview.isLoading,
        error,
        retry,
        presets: {
          records: presets.data?.records || [],
          isLoading: presets.isLoading,
        },
        setPresetDialog,
        setColumnDialog,
        setState,
      }}>
      {children}
    </DashboardsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDashboards = () => useContext(DashboardsContext);
